'use client';

import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import type { BffCatalogSubscriptionsResponse } from '@/core/models/bff-catalog';
import useAuthSession from '@/core/hooks/use-auth-session';
import {
  bffCatalogSubscriptionsQueryKey,
  type BffCatalogSubscriptionsQueryParams,
  isBffCatalogEnabledForLocale,
  localeToBffCatalogParams,
} from '@/core/queries/bff/catalog/bff-catalog-query-keys';
import fetchBffCatalogSubscriptions from '@/core/queries/bff/catalog/fetch-bff-catalog-subscriptions';

export { default as fetchBffCatalogSubscriptions } from '@/core/queries/bff/catalog/fetch-bff-catalog-subscriptions';

const useBffCatalogSubscriptionsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<BffCatalogSubscriptionsResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const locale = useLocale();
  const hasAuthSession = useAuthSession();
  const { countryCode, languageCode } = localeToBffCatalogParams(locale);
  const params: BffCatalogSubscriptionsQueryParams = {
    countryCode,
    languageCode,
    hasAccessToken: hasAuthSession,
  };

  const isCatalogEnabled = isBffCatalogEnabledForLocale(locale);

  return useQuery({
    ...queryOptions,
    queryKey: bffCatalogSubscriptionsQueryKey(params),
    queryFn: () => fetchBffCatalogSubscriptions(params),
    enabled: isCatalogEnabled && (queryOptions?.enabled ?? true),
  });
};

export default useBffCatalogSubscriptionsQuery;
